import { Location } from '@cobliteam/common-types/src/location';
import type { Preferences } from '@cobliteam/common-types/src/preferences';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { useLatestNews } from '@cobliteam/hooks/src/useEntity/usePrismic';
import { useLanguage, useTranslation } from '@cobliteam/i18n';
import { isFilled, PrismicImage, PrismicLink, PrismicRichText } from '@cobliteam/prismic';
import Button from '@cobliteam/ui/components/Button';
import Drawer, { DrawerBody, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from '@cobliteam/ui/components/Drawer';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import { useLocalStorage } from '@cobliteam/ui/hooks';
import Icon from '@cobliteam/ui/icons';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router-dom';
import { getAnimatedIcon } from '../../AppSidebar/components/animatedIcons';

const SKELETON_ITEMS = ['first', 'second', 'third', 'fourth'];
const NEWS_ITEMS_LIMIT = 10;
const NEWS_IMAGE_WIDTHS = [224, 448];
const NEWS_IMAGE_PARAMS = { q: 75 };

const getNewsTimestamp = (date: string | undefined) => {
  if (!date) return 0;
  const timestamp = new Date(date).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const formatNewsDate = (date: string | undefined, locale: string) => {
  if (!date) return null;
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return null;

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsedDate);
};

const NewsFeedFallback = () => {
  const { t } = useTranslation('common');
  return <Button variant='surface' color='danger' size='sm' icon={<Icon name='x' />} aria-label={t('news_feed.open')} />;
};

const NewsFeedLoading = () => (
  <div className='flex flex-col gap-2' aria-hidden='true'>
    {SKELETON_ITEMS.map((item) => (
      <div key={item} className='flex gap-3 rounded-xl border border-gray-6 bg-white p-3'>
        <div className='h-20 w-28 shrink-0 animate-pulse rounded-lg bg-gray-4' />
        <div className='flex flex-1 flex-col gap-2 py-1'>
          <div className='h-4 w-3/4 animate-pulse rounded bg-gray-4' />
          <div className='h-3 w-full animate-pulse rounded bg-gray-3' />
          <div className='h-3 w-24 animate-pulse rounded bg-gray-3' />
        </div>
      </div>
    ))}
  </div>
);

type PrismicNewsItemProps = {
  item: NonNullable<ReturnType<typeof useLatestNews>['data']>[number];
  isNew: boolean;
  locale: string;
};

const PrismicNewsItem = ({ item, isNew, locale }: PrismicNewsItemProps) => {
  const { t } = useTranslation('common');
  const formattedDate = formatNewsDate(item.last_publication_date, locale);
  const hasImage = isFilled.image(item.data.image);

  return (
    <PrismicLink
      field={item.data.link}
      target='_blank'
      rel='noopener noreferrer'
      className={`group flex w-full gap-3 rounded-xl border p-3 text-left no-underline outline-none transition-[border-color,background-color,box-shadow] hover:border-primary-7 hover:bg-primary-1 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary-8 focus-visible:ring-offset-2 ${isNew ? 'border-primary-6 bg-primary-1' : 'border-gray-6 bg-white'}`}
    >
      {hasImage ? (
        <PrismicImage field={item.data.image} alt='' className='h-20 w-28 shrink-0 rounded-lg bg-gray-3 object-cover' widths={NEWS_IMAGE_WIDTHS} imgixParams={NEWS_IMAGE_PARAMS} />
      ) : (
        <span className='flex h-20 w-28 shrink-0 items-center justify-center rounded-lg bg-primary-3 text-primary-10' aria-hidden='true'>
          <Icon name='megaphone' className='size-6' />
        </span>
      )}

      <span className='flex min-w-0 flex-1 flex-col self-stretch'>
        <span className='font-semibold text-[11px] text-primary-9 uppercase tracking-wide'>{item.data.type || t('news_feed.default_type')}</span>
        <span className='mt-1 flex items-start justify-between gap-2'>
          <PrismicRichText field={item.data.title} className='line-clamp-2 font-semibold text-gray-12 text-sm leading-5' />
          {isNew ? (
            <span className='shrink-0 rounded-full bg-primary-3 px-2 py-0.5 font-semibold text-[10px] text-primary-10 uppercase tracking-wide'>{t('news_feed.new')}</span>
          ) : null}
        </span>
        <PrismicRichText field={item.data.resume} className='mt-1 line-clamp-2 text-gray-10 text-xs leading-4' />
        {formattedDate ? (
          <time dateTime={item.last_publication_date} className='mt-auto pt-2 text-gray-9 text-xs'>
            {formattedDate}
          </time>
        ) : null}
      </span>

      <Icon
        name='chevron-right'
        aria-hidden='true'
        className='mt-auto mb-auto size-4 shrink-0 text-gray-8 transition-transform group-hover:translate-x-0.5 group-hover:text-primary-9 motion-reduce:transition-none'
      />
    </PrismicLink>
  );
};

const NewsFeedContent = () => {
  const history = useHistory();
  const { t } = useTranslation('common');
  const { currentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { data: news = [], isLoading, error } = useLatestNews({ limit: NEWS_ITEMS_LIMIT });
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');

  const recentNews = [...news].sort((left, right) => getNewsTimestamp(right.last_publication_date) - getNewsTimestamp(left.last_publication_date)).slice(0, NEWS_ITEMS_LIMIT);
  const latestNewsTimestamp = getNewsTimestamp(recentNews[0]?.last_publication_date);
  const storedLastOpenedTimestamp = preferences?.lastNewsFeedOpenedDate ? Number(preferences.lastNewsFeedOpenedDate) : 0;
  const lastOpenedTimestamp = Number.isFinite(storedLastOpenedTimestamp) ? storedLastOpenedTimestamp : 0;
  const [highlightAfterTimestamp, setHighlightAfterTimestamp] = useState(lastOpenedTimestamp);
  const hasUnreadNews = latestNewsTimestamp > lastOpenedTimestamp;

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    if (nextOpen) {
      setHighlightAfterTimestamp(lastOpenedTimestamp);
      setPreferences({ ...preferences, lastNewsFeedOpenedDate: latestNewsTimestamp || Date.now() });
    }
  };

  const handleSeeAll = () => {
    handleOpenChange(false);
    history.push(Location.NEWS);
  };

  return (
    <>
      <Tooltip content={t('news_feed.title')} asChild position='right'>
        <div className='relative mt-0.5' onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
          <Button
            data-testid='app-header-newslist-button'
            className='text-default hover:text-brand'
            variant={isOpen || !hasUnreadNews ? 'surface' : 'ghost'}
            color='primary'
            size='sm'
            icon={getAnimatedIcon({ icon: 'gift', isHovered })}
            aria-label={t('news_feed.open')}
            aria-expanded={isOpen}
            onClick={() => handleOpenChange(true)}
          />
          {hasUnreadNews ? (
            <span className='absolute -top-0.5 -right-0.5 flex size-2' aria-hidden='true'>
              <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-75 motion-reduce:animate-none' />
              <span className='relative inline-flex size-2 rounded-full bg-brand' />
            </span>
          ) : null}
        </div>
      </Tooltip>

      <Drawer open={isOpen} onOpenChange={handleOpenChange}>
        <DrawerContent size='md' className='max-w-[calc(100vw-16px)]' data-testid='news-feed-drawer'>
          <DrawerHeader>
            <DrawerTitle className='truncate'>{t('news_feed.title')}</DrawerTitle>
            <div className='flex shrink-0 items-center gap-1'>
              <Button variant='link' size='sm' endIcon={<Icon name='arrow-up-right' className='size-4' />} onClick={handleSeeAll}>
                {t('news_feed.see_all')}
              </Button>
              <DrawerClose />
            </div>
          </DrawerHeader>

          <DrawerBody className='min-h-0 flex-1 flex-col bg-gray-1 px-5 py-5'>
            <div className='flex flex-col gap-4'>
              <p className='text-gray-11 text-sm leading-5'>{t('news_feed.description')}</p>

              {isLoading ? <NewsFeedLoading /> : null}

              {!isLoading && error ? (
                <div className='rounded-xl border border-danger-6 bg-white px-5 py-8 text-center text-danger-10 text-sm'>{t('news_feed.load_error')}</div>
              ) : null}

              {!(isLoading || error) && recentNews.length === 0 ? (
                <div className='flex flex-col items-center rounded-xl border border-gray-6 border-dashed bg-white px-6 py-12 text-center'>
                  <Icon name='megaphone' aria-hidden='true' className='size-7 text-gray-8' />
                  <p className='mt-3 text-gray-10 text-sm'>{t('news_feed.empty')}</p>
                </div>
              ) : null}

              {isLoading || error || recentNews.length === 0 ? null : (
                <div className='flex flex-col gap-2'>
                  {recentNews.map((item) => (
                    <PrismicNewsItem key={item.uid} item={item} isNew={getNewsTimestamp(item.last_publication_date) > highlightAfterTimestamp} locale={currentLanguage} />
                  ))}
                </div>
              )}
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

const NewsFeed = () => {
  return (
    <ErrorBoundary
      fallback={<NewsFeedFallback />}
      onError={(error, errorInfo) => {
        DatadogTrack.error(error, {
          component: 'NewsFeed',
          section: 'AppHeader',
          errorInfo,
        });
      }}
    >
      <NewsFeedContent />
    </ErrorBoundary>
  );
};

export default NewsFeed;
